/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Autocomplete,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { notificationActions } from 'modules/common/notifications/slice';
import { useEffect, useState } from 'react';
import { X } from 'react-feather';
import { selectHotelGroups } from 'modules/hotel-set/selectors';
import { hotelSetActions } from 'modules/hotel-set/slice';
import { toast } from 'react-toastify';
import CustomToggleSwitch from 'modules/common/components/toggle-switch';
import AggregationTransferList from '../add-hotel-group-transfer-list';
import QueryBox from '../query-box';
import SaveFilterDialog from '../save-filter';
import { selectNotification } from '../../../common/notifications/selectors';
import ERROR_TYPES from '../../../common/constants/error-types';
import TOAST_TYPES from '../../../common/constants/toast-types';

/**
 * Custom aggregation filter component is implemented
 * @param {Boolean} open - To open/ close dialog box
 * @param {Function} onClose - Function to trigger on close event
 * @returns
 */
const AddHotelGroup = ({
  open,
  onClose,
  hotelList,
  selectedHotelGroupName,
  isEditable = false,
  setIsEditable = () => {},
}) => {
  const dispatch = useDispatch();
  //
  const [groupHotelList, setGroupHotelList] = useState([]);
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const [loadFilter, setLoadFilter] = useState(false);
  const [temGroupList, setTemGroupList] = useState([]);
  const filters = useSelector(selectHotelGroups);
  const notification = useSelector(selectNotification);
  //
  const [loadList, setLoadList] = useState([]);

  useEffect(() => {
    if (filters?.results?.length > 0) {
      const list = filters?.results?.map((a) => ({
        id: a?.hotelGroups?.hotelGroupName,
        label: a?.hotelGroups?.hotelGroupName,
        query: a?.hotelGroups?.hotelQuery,
      }));

      setLoadList(list);
    }
  }, [filters]);
  //
  useEffect(() => {
    if (isEditable && loadList?.length > 0 && open) {
      const selectedGroup = loadList?.find((a) => a.label === selectedHotelGroupName);
      setLoadFilter(true);
      dispatch(hotelSetActions.setHotelGroupQuery(selectedGroup?.query?.trim()));
      dispatch(hotelSetActions.setSelectedHotelGroupName(selectedGroup?.label));
    }
  }, [isEditable, loadList, selectedHotelGroupName, open]);
  // Triggered once when the component mounted
  useEffect(() => {
    dispatch(notificationActions.resetNotification());
  }, []);

  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      toast(notification?.message, { type: TOAST_TYPES.SUCCESS });
      dispatch(notificationActions.resetNotification());
    }
  }, [notification]);
  //
  useEffect(() => {
    // Update global and local states
    if (open && !isEditable) {
      setGroupHotelList([]);
      setTemGroupList([]);
      dispatch(hotelSetActions.setHotelGroupQuery(''));
      dispatch(hotelSetActions.setSelectedHotelGroupName(''));
    }
  }, [open, isEditable]);
  //
  const modalAction = (reason, action) => {
    if (reason && reason === 'backdropClick') return;
    onClose(action);
  };
  //
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => modalAction(reason, false)}
      aria-labelledby="form-dialog-title"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '800px',
          },
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 2 }}>
        <Grid container item flexDirection="row" justifyContent="space-between" xs={12}>
          <Grid item xs={3.5}>
            <Typography variant="h5">{isEditable ? 'Update' : 'Add'} Hotel Set</Typography>
          </Grid>

          <Grid item xs={5}>
            <Autocomplete
              sx={{
                minWidth: 150,
              }}
              options={loadList ?? []}
              getOptionLabel={(option) => (option.label ? option.label : '')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              onChange={(event, value) => {
                setLoadFilter(true);
                dispatch(hotelSetActions.setHotelGroupQuery(value?.query));
                dispatch(hotelSetActions.setSelectedHotelGroupName(value?.label));
              }}
              disableClearable
              disabled={loadList?.length < 1}
              renderInput={(params) => (
                <TextField {...params} label="Hotel Sets" size="small" color="primary" />
              )}
            />
          </Grid>
          <Grid item xs={3.5}>
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => {
                modalAction(false);
                setIsEditable(false);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <X />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            borderRadius: '8px',
            borderColor: '#8c8787 !important',
            border: 1,
            padding: 4,
          }}
        >
          <AggregationTransferList
            focusOnList={hotelList}
            groupList={groupHotelList}
            setGroupList={(value) => setGroupHotelList(value)}
            isQueryEnabled={isQueryEnabled}
            temGroupList={temGroupList}
            setTemGroupList={setTemGroupList}
          />
        </Box>
        <Box
          sx={{
            borderRadius: '8px',
            borderColor: '#8c8787 !important',
            border: 1,
            padding: 2,
            marginBottom: 4,
            marginTop: 4,
          }}
        >
          <CustomToggleSwitch
            label="Advanced Querying"
            title=""
            onChange={(event) => setIsQueryEnabled(event.target.checked)}
            checked={isQueryEnabled}
          />

          <QueryBox
            setGroupList={setGroupHotelList}
            onClose={() => {
              modalAction(false);
            }}
            open={open}
            isQueryEnabled={isQueryEnabled}
            loadFilter={loadFilter}
            setLoadFilter={setLoadFilter}
            focusOnList={hotelList}
            setTemGroupList={setTemGroupList}
          />
        </Box>
        <SaveFilterDialog
          groupList={groupHotelList}
          hotelList={hotelList}
          setGroupList={setGroupHotelList}
          setTemGroupList={setTemGroupList}
          onClose={() => {
            modalAction(false);
            setIsEditable(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
//
export default AddHotelGroup;
